import React, { useMemo, useState } from "react";
import { Chip, DateField, LivePillRow, PaceSlider, ScreenShell, WheelPicker } from "../components";
import { T } from "../theme";
import {
  bmrFor,
  cautionPaceForGoal,
  dailyTargetFor,
  effectivePaceLbsPerWeek,
  maxPaceForGoal,
  pacePresetsForGoal,
  projectedEndDateYmd,
  tdeeFor,
} from "../calc";
import { addDaysYMD, todayISO } from "../../lib/date";
import { displayWeight } from "../units";
import type { GoalDirection, OnboardingDraft, PacePreset } from "../types";

type Props = {
  step: number;
  total: number;
  draft: OnboardingDraft;
  goal: GoalDirection;
  onPaceChange: (lbsPerWeek: number, preset?: PacePreset) => void;
  onNext: () => void;
  onBack: () => void;
  isMobile: boolean;
};

type Mode = "pace" | "date";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MIN_DAYS_AHEAD = 21;
const MAX_DAYS_AHEAD = 730;
const MIN_PACE = 0.1;

function fmtFromToday(ymd: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(ymd + "T00:00:00");
  const days = Math.round((target.getTime() - today.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days < 14) return days === 1 ? "in 1 day" : `in ${days} days`;
  const weeks = Math.round(days / 7);
  if (weeks < 9) return `in ${weeks} weeks`;
  const months = Math.round(days / 30);
  return `in ${months} months`;
}

function fmtDate(ymd: string): string {
  const d = new Date(ymd + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function paceFromDays(currentLbs: number, targetLbs: number, days: number): number {
  if (days <= 0) return 0;
  const delta = Math.abs(targetLbs - currentLbs);
  return delta / (days / 7);
}

function daysInMonth(year: number, monthIdx: number): number {
  return new Date(year, monthIdx + 1, 0).getDate();
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function parseYmd(ymd: string): { y: number; mo: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  return { y: parseInt(m[1], 10), mo: parseInt(m[2], 10) - 1, d: parseInt(m[3], 10) };
}

export function PaceScreen({ step, total, draft, goal, onPaceChange, onNext, onBack, isMobile }: Props): React.ReactElement {
  const today = todayISO();
  const minDateYmd = addDaysYMD(today, MIN_DAYS_AHEAD) ?? today;
  const maxDateYmd = addDaysYMD(today, MAX_DAYS_AHEAD) ?? today;

  // Derived end date — uses the *effective* pace (after the safety floor on
  // intake) so the displayed date matches what's actually achievable on a
  // safe budget. Without this, picking an aggressive pace would show an
  // overly-optimistic date that diverges from the saved plan once the
  // intake gets floored.
  const computedEndYmd = useMemo(() => {
    if (goal === "maintain") return "";
    const sex = draft.sex ?? "X";
    const bmr = bmrFor({ weightLbs: draft.currentWeightLbs, heightIn: draft.heightIn, sex, birthday: draft.birthday }) ?? 0;
    const tdee = bmr > 0 ? tdeeFor(bmr, draft.activityFactor) : 0;
    const effPace = tdee > 0
      ? effectivePaceLbsPerWeek({ paceLbsPerWeekAbs: draft.paceLbsPerWeekAbs, tdee, sex, goal })
      : draft.paceLbsPerWeekAbs;
    return projectedEndDateYmd(draft.currentWeightLbs, draft.targetWeightLbs, effPace);
  }, [
    goal,
    draft.sex,
    draft.birthday,
    draft.heightIn,
    draft.activityFactor,
    draft.currentWeightLbs,
    draft.targetWeightLbs,
    draft.paceLbsPerWeekAbs,
  ]);

  const [mode, setMode] = useState<Mode>("pace");
  const [dateYmd, setDateYmd] = useState<string>(computedEndYmd || (addDaysYMD(today, 84) ?? today));

  const maxPace = maxPaceForGoal(goal);
  const cautionPace = cautionPaceForGoal(goal);

  function setDateAndDerivePace(nextYmd: string): void {
    setDateYmd(nextYmd);
    const dt = new Date(nextYmd + "T00:00:00");
    const td = new Date(today + "T00:00:00");
    const days = Math.max(1, Math.round((dt.getTime() - td.getTime()) / 86400000));
    const lbsPerWeek = paceFromDays(draft.currentWeightLbs, draft.targetWeightLbs, days);
    const clamped = Math.min(maxPace, Math.max(MIN_PACE, lbsPerWeek));
    onPaceChange(clamped);
  }

  function setModeWith(m: Mode): void {
    setMode(m);
  }

  const { dailyTarget } = useMemo(() => {
    const sex = draft.sex ?? "X";
    const bmr = bmrFor({
      weightLbs: draft.currentWeightLbs,
      heightIn: draft.heightIn,
      sex,
      birthday: draft.birthday,
    }) ?? 0;
    const tdee = bmr > 0 ? tdeeFor(bmr, draft.activityFactor) : 0;
    const target = tdee > 0 ? dailyTargetFor({ goal, paceLbsPerWeek: draft.paceLbsPerWeekAbs, tdee, sex }) : 0;
    return { dailyTarget: target };
  }, [draft, goal]);

  const presets = pacePresetsForGoal(goal);
  const inCaution = draft.paceLbsPerWeekAbs >= cautionPace;

  // The top pill always shows the *achievable* end date. The warning below
  // fires only when the user's picked date genuinely requires going faster
  // than the safe maxPace cap — that's the only case where the "below the
  // safety floor" message is accurate. Earlier this compared computedEnd
  // vs dateYmd with a 2-day tolerance, which fired spuriously on rounding
  // drift even when no clamping had actually happened.
  const dateClamp = mode === "date" && goal !== "maintain" && dateYmd
    ? (() => {
        const dt = new Date(dateYmd + "T00:00:00");
        const td = new Date(today + "T00:00:00");
        const days = Math.max(1, Math.round((dt.getTime() - td.getTime()) / 86400000));
        return paceFromDays(draft.currentWeightLbs, draft.targetWeightLbs, days) > maxPace;
      })()
    : false;
  const displayedEndYmd =
    goal === "maintain"
      ? ""
      : mode === "date"
        ? computedEndYmd
        : computedEndYmd;

  // Subtitle anchors the user to the actual delta they're optimizing for.
  const fromW = displayWeight(draft.currentWeightLbs, draft.unitsWeight);
  const toW = displayWeight(draft.targetWeightLbs, draft.unitsWeight);
  const subtitle =
    goal === "maintain"
      ? `Holding at ${fromW} ${draft.unitsWeight}.`
      : `Going from ${fromW} → ${toW} ${draft.unitsWeight}.`;

  return (
    <ScreenShell
      step={step}
      total={total}
      title={goal === "maintain" ? "Confirm your plan" : "How fast — or by when?"}
      subtitle={subtitle}
      topPills={
        <LivePillRow
          primary={dailyTarget > 0 ? `${dailyTarget.toLocaleString()}` : "—"}
          primaryLabel="daily budget · kcal"
          secondary={displayedEndYmd ? fmtDate(displayedEndYmd) : "Ongoing"}
          secondaryLabel="goal date"
        />
      }
      onBack={onBack}
      isMobile={isMobile}
      primaryLabel="See my plan"
      onPrimary={onNext}
    >
      {goal !== "maintain" ? (
        <div style={{ display: "grid", gap: 14 }}>
          {/* Section header: eyebrow + small toggle on the right */}
          {mode === "pace" ? (
            <>
              <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
                {presets.map((p) => (
                  <Chip
                    key={p.preset}
                    active={draft.pacePreset === p.preset}
                    caution={p.caution}
                    onClick={() => onPaceChange(p.lbsPerWeek, p.preset)}
                  >
                    {p.label}
                  </Chip>
                ))}
              </div>
              <div>
                <PaceSlider
                  value={draft.paceLbsPerWeekAbs}
                  min={MIN_PACE}
                  max={maxPace}
                  step={0.05}
                  cautionFrom={cautionPace}
                  onChange={(v) => onPaceChange(v)}
                />
                <div
                  style={{
                    marginTop: 14,
                    textAlign: "center",
                    fontSize: "1.7rem",
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    ...T.tabular,
                  }}
                >
                  {goal === "lose" ? "−" : "+"}
                  {draft.paceLbsPerWeekAbs.toFixed(2)}
                  <span style={{ fontSize: "0.9rem", color: T.muted, fontWeight: 700, marginLeft: 6 }}>lb/wk</span>
                </div>
                {inCaution ? (
                  <div
                    style={{
                      marginTop: 12,
                      padding: "10px 14px",
                      borderRadius: 12,
                      background: "rgba(245,158,11,0.10)",
                      border: "1px solid rgba(245,158,11,0.35)",
                      color: "#fde68a",
                      fontSize: "0.82rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {goal === "gain"
                      ? `Faster than ${cautionPace.toFixed(1)} lb/wk is mostly fat gain. We'll cap at the safety floor.`
                      : `Above ${cautionPace.toFixed(1)} lb/wk is hard to sustain and risks losing muscle. We'll cap the budget at a safety floor.`}
                  </div>
                ) : null}
              </div>
              <ModeSwitchLink mode="pace" onSwitch={() => setModeWith("date")} />
            </>
          ) : (
            <>
              <DatePickerBlock
                ymd={dateYmd}
                minYmd={minDateYmd}
                maxYmd={maxDateYmd}
                onChange={setDateAndDerivePace}
                isMobile={isMobile}
              />
              <div
                style={{
                  textAlign: "center",
                  fontSize: "0.84rem",
                  fontWeight: 600,
                  color: T.fg2,
                  marginTop: -4,
                }}
              >
                {fmtFromToday(dateYmd)}
              </div>
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.025)",
                  border: `1px solid ${T.hairline}`,
                  borderLeft: `3px solid ${T.lime}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <div style={{ fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.muted }}>
                  That works out to
                </div>
                <div style={{ fontSize: "1.1rem", fontWeight: 800, ...T.tabular, letterSpacing: "-0.01em" }}>
                  {goal === "lose" ? "−" : "+"}
                  {draft.paceLbsPerWeekAbs.toFixed(2)}
                  <span style={{ fontSize: "0.7rem", color: T.muted, fontWeight: 700, marginLeft: 4 }}>lb/wk</span>
                </div>
              </div>
              {dateClamp && computedEndYmd ? (
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: 12,
                    background: "rgba(245,158,11,0.10)",
                    border: "1px solid rgba(245,158,11,0.35)",
                    color: "#fde68a",
                    fontSize: "0.82rem",
                    lineHeight: 1.5,
                  }}
                >
                  To hit that date you'd need to eat below the safety floor. We've slowed the
                  plan to a safe pace; goal reached <span style={{ fontWeight: 800 }}>{fmtDate(computedEndYmd)}</span>{" "}
                  instead.
                </div>
              ) : null}
              <ModeSwitchLink mode="date" onSwitch={() => setModeWith("pace")} />
            </>
          )}
        </div>
      ) : (
        <div
          style={{
            padding: "18px 20px",
            borderRadius: 16,
            background: "rgba(255,255,255,0.025)",
            border: `1px solid ${T.hairline}`,
            color: T.fg2,
            fontSize: "0.92rem",
            lineHeight: 1.5,
          }}
        >
          Your daily target will recalibrate weekly based on your actual weigh-ins and (if connected) your active calories
          from Apple Health.
        </div>
      )}
    </ScreenShell>
  );
}

function ModeSwitchLink({ mode, onSwitch }: { mode: Mode; onSwitch: () => void }): React.ReactElement {
  const label = mode === "pace" ? "Pick a date instead →" : "← Set pace instead";
  return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 6 }}>
      <button
        type="button"
        onClick={onSwitch}
        style={{
          background: "transparent",
          border: "none",
          color: T.fg2,
          fontSize: "0.86rem",
          fontWeight: 600,
          cursor: "pointer",
          padding: "8px 12px",
          letterSpacing: "-0.005em",
        }}
      >
        {label}
      </button>
    </div>
  );
}

function DatePickerBlock({
  ymd,
  minYmd,
  maxYmd,
  onChange,
  isMobile,
}: {
  ymd: string;
  minYmd: string;
  maxYmd: string;
  onChange: (next: string) => void;
  isMobile: boolean;
}): React.ReactElement {
  const parts = parseYmd(ymd) ?? parseYmd(minYmd)!;
  const minP = parseYmd(minYmd)!;
  const maxP = parseYmd(maxYmd)!;

  if (!isMobile) {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <DateField
          value={ymd}
          min={minYmd}
          max={maxYmd}
          onChange={onChange}
          placeholder="Pick a target date"
        />
      </div>
    );
  }

  const yearValues: string[] = [];
  for (let y = minP.y; y <= maxP.y; y += 1) yearValues.push(String(y));
  const monthValues = MONTHS;
  const daysCount = daysInMonth(parts.y, parts.mo);
  const dayValues = Array.from({ length: daysCount }, (_, i) => String(i + 1));

  function emit(y: number, m: number, d: number): void {
    const safeDay = Math.min(d, daysInMonth(y, m));
    let ymdNext = `${y}-${pad(m + 1)}-${pad(safeDay)}`;
    if (ymdNext < minYmd) ymdNext = minYmd;
    if (ymdNext > maxYmd) ymdNext = maxYmd;
    onChange(ymdNext);
  }

  return (
    <WheelPicker
      rowHeight={36}
      columns={[
        { values: monthValues, selectedIndex: parts.mo, onChange: (i) => emit(parts.y, i, parts.d) },
        { values: dayValues, selectedIndex: Math.min(daysCount - 1, parts.d - 1), onChange: (i) => emit(parts.y, parts.mo, i + 1) },
        { values: yearValues, selectedIndex: Math.max(0, yearValues.indexOf(String(parts.y))), onChange: (i) => emit(parseInt(yearValues[i], 10), parts.mo, parts.d) },
      ]}
    />
  );
}
